/**
 * useWorkspaceResolver Hook
 *
 * Resolves the current workspace state for authenticated users.
 * Returns a discriminated union describing what the app should do:
 * - loading: still fetching workspace data
 * - setup-pending: webhook hasn't created the workspace yet
 * - no-workspaces: user genuinely has zero workspaces
 * - resolved: workspace selected and ready to use
 *
 * This hook does NOT poll for webhook completion — that responsibility
 * belongs to the setup-pending page. This hook is a pure "check once
 * and report the current state" mechanism.
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';

import { isWorkspaceReady } from '@/hooks/useCreateFirstWorkspace';
import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';

export type WorkspaceResolverState =
  | { status: 'loading' }
  | { status: 'setup-pending' }
  | { status: 'no-workspaces' }
  | { status: 'error'; error: Error }
  | { status: 'resolved'; workspaceId: string };

export function useWorkspaceResolver(): WorkspaceResolverState {
  const rpc = useRpc();
  const { user } = useUser();
  const { selectedWorkspaceId, setSelectedWorkspaceId, clearSelectedWorkspace } = useWorkspaceStore();

  // Track whether the resolver previously reached 'resolved' state.
  // Used to distinguish access revocation (was resolved, now 0 workspaces)
  // from fresh signup (never resolved, webhook pending).
  const wasResolvedRef = useRef(false);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.workspaces.resolver,
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.all.$get();
      if (!res.ok) throw new Error('Failed to fetch workspaces');
      return await res.json() as {
        workspaces: { id: string }[];
        last_selected_workspace_id: string | null;
      };
    },
    // Don't throw to error boundary — we handle all states in the layout
    throwOnError: false,
  });

  const workspaceReady = isWorkspaceReady(user);

  // Auto-select a workspace when data arrives
  useEffect(() => {
    if (!data || data.workspaces.length === 0) return;

    const workspaceIds = data.workspaces.map((w) => w.id);

    // Current selection is still valid
    if (selectedWorkspaceId && workspaceIds.includes(selectedWorkspaceId)) return;

    // Prefer last_selected from DB, then fall back to first
    const newId =
      data.last_selected_workspace_id && workspaceIds.includes(data.last_selected_workspace_id)
        ? data.last_selected_workspace_id
        : data.workspaces[0].id;
    setSelectedWorkspaceId(newId);
  }, [data, selectedWorkspaceId, setSelectedWorkspaceId]);

  // Clear workspace store when user genuinely has no workspaces
  useEffect(() => {
    if (data && data.workspaces.length === 0) {
      clearSelectedWorkspace();
    }
  }, [data, clearSelectedWorkspace]);

  // Compute state
  if (isLoading) return { status: 'loading' };

  // Query failed with no cached data — surface the error
  if (error && !data) return { status: 'error', error: error as Error };

  if (data && data.workspaces.length === 0) {
    // If user was previously in a workspace, this is access revocation —
    // go straight to no-workspaces (not setup-pending).
    // setup-pending is only for brand-new users whose webhook hasn't fired yet.
    if (wasResolvedRef.current || workspaceReady) {
      return { status: 'no-workspaces' };
    }
    return { status: 'setup-pending' };
  }

  if (selectedWorkspaceId) {
    // Validate the persisted selection against the fetched workspace list.
    // After access revocation, the Zustand-persisted ID may reference a workspace
    // the user can no longer access. Returning 'resolved' with a stale ID would
    // cause AppShellContent to mount and fire queries that all return 403.
    // Instead, return 'loading' so the auto-select effect can fix the selection.
    if (data) {
      const isValid = data.workspaces.some((w) => w.id === selectedWorkspaceId);
      if (!isValid) return { status: 'loading' };
    }
    wasResolvedRef.current = true;
    return { status: 'resolved', workspaceId: selectedWorkspaceId };
  }

  // Workspace selection effect hasn't fired yet (data arrived but effect is pending)
  return { status: 'loading' };
}
